<template>
  <UiComponentWrapper class="page">
    <!-- Section > Hero -->
    <UiComponentContainer class="jumbotron">
      <UiComponentWrapper class="hero">
        <UiComponentWrapper class="tags">
          <UiComponentWrapper
            v-for="{ id, tag } in props.page.heroSubCategories"
            :key="`tag-${id}`"
            class="tag"
          >
            {{ tag }}
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentHeading>{{ props.page.heroTitle }}</UiComponentHeading>
        <UiComponentParagraph>{{ props.page.heroText }}</UiComponentParagraph>
        <UiComponentWrapper class="metadata">
          <UiComponentWrapper class="item">
            <UiComponentIcon type="timer" />
            <UiComponentParagraph>
              {{
                $interpolate(props.componentWording.duration, {
                  "%{duration}": props.page.heroDuration,
                })
              }}
            </UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper class="item">
            <UiComponentIcon type="users" />
            <UiComponentParagraph>
              {{
                $interpolate(props.componentWording.participants, {
                  "%{numberOfParticipants}": props.page.heroParticipants,
                })
              }}
            </UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper class="item">
            <UiComponentIcon type="office" />
            <UiComponentParagraph>
              {{
                $interpolate(props.componentWording.levelOfOrganization, {
                  "%{levelOfOrganisation}": props.page.heroLevelOfOrganization,
                })
              }}
            </UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper class="item">
            <UiComponentIcon type="empty-star" />
            <UiComponentParagraph>
              {{
                $interpolate(props.componentWording.featureNeeded, {
                  "%{featureName}": props.page.heroFeatureNeeded.shortTitle || props.page.heroFeatureNeeded.title,
                })
              }}
            </UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="props.page.heroExamples.length >= 3"
        class="examples"
      >
        <UiComponentWrapper
          v-for="({ id, text }, index) in props.page.heroExamples"
          :key="`example-card-${id}`"
          ref="$examples"
          class="card"
          :class="[props.page.color, props.page.pattern]"
        >
          <UiComponentWrapper class="inner">
            <UiComponentWrapper class="head">
              {{ props.componentWording.example }}
            </UiComponentWrapper>
            <UiComponentWrapper class="body">
              <UiComponentParagraph class="question">
                {{ text }}
              </UiComponentParagraph>
              <UiComponentWrapper
                class="copy"
                :class="{ copied }"
                :data-copied="props.componentWording.copied"
                @click="copyToClipboard(text)"
                @mouseleave="copied = false"
              >
                <UiComponentIcon type="copy" />
                <UiComponentParagraph>{{ props.componentWording.copy }}</UiComponentParagraph>
              </UiComponentWrapper>
            </UiComponentWrapper>
            <UiComponentWrapper class="footer">
              <UiComponentWrapper class="navigation">
                <UiComponentIcon
                  type="chevron-left"
                  @click="showPreviousExample"
                />
                <UiComponentParagraph class="status">
                  {{ index+1 }}/{{ props.page.heroExamples.length }}
                </UiComponentParagraph>
                <UiComponentIcon
                  type="chevron-right"
                  @click="showNextExample"
                />
              </UiComponentWrapper>
              <UiComponentWrapper class="logo">
                <img
                  alt="Livestorm logo"
                  loading="lazy"
                  height="32"
                  src="~/assets/svg/livestorm-icon.svg"
                  width="32"
                >
              </UiComponentWrapper>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-else
        class="illustrations"
      >
        <UiComponentWrapper
          v-for="n in 3"
          :key="`placeholder-card-${n}`"
          class="card"
          :class="props.page.pattern"
        >
          <UiComponentWrapper class="outer">
            <UiComponentWrapper class="inner">
              <img
                alt="Livestorm logo"
                class="logo"
                height="20"
                loading="lazy"
                src="@/assets/svg/logo-white.svg"
                width="133"
              >
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Main -->
    <UiComponentContainer class="content">
      <UiComponentWrapper class="main">
        <StructuredText
          :data="props.page.content"
          :render-block="renderBlock"
        />
      </UiComponentWrapper>
      <UiComponentWrapper class="aside">
        <UiComponentWrapper
          class="try"
          @click.stop="navigateToSignupOrUpgrade"
        >
          <UiComponentWrapper
            v-if="props.page.heroFeatureNeeded.defaultImage"
            class="head"
          >
            <UiComponentImage
              :alt="props.page.heroFeatureNeeded.defaultImage.alt"
              :class="{ 'zoom-out': props.page.heroFeatureNeeded.defaultImage.format === 'png' }"
              :expected-size="202"
              :height="props.page.heroFeatureNeeded.defaultImage.height"
              :orientation="props.page.heroFeatureNeeded.defaultImage.width >= props.page.heroFeatureNeeded.defaultImage.height ? 'portrait' : 'landscape'"
              :source="props.page.heroFeatureNeeded.defaultImage.url"
              :width="props.page.heroFeatureNeeded.defaultImage.width"
            />
          </UiComponentWrapper>
          <UiComponentWrapper class="body">
            <UiComponentHeading
              as="paragraph"
              :level="5"
            >
              {{
                $interpolate(props.componentWording.tryTheFeature, {
                  "%{featureName}": (props.page.heroFeatureNeeded.shortTitle || props.page.heroFeatureNeeded.title).toLowerCase(),
                })
              }}
            </UiComponentHeading>
            <AtomButton
              :action="signupOrUpgradeCallToAction.action"
              label="Sidebar section CTA"
              :to="signupOrUpgradeCallToAction.to"
              type="text"
            >
              {{ signupOrUpgradeCallToAction.slot }}
              <UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>

        <OrganismContentCard
          v-if="!!sidebarCTACard"
          as="paragraph"
          class="resource"
          :content="sidebarCTACard"
        />
      </UiComponentWrapper>
      <UiComponentWrapper class="footer">
        <OrganismContentCard
          v-if="!!sidebarCTACard"
          as="paragraph"
          class="resource"
          :content="sidebarCTACard"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > You may like -->
    <UiComponentContainer
      v-if="relatedIcebreakers.length > 0"
      class="you-may-like"
    >
      <UiComponentWrapper class="title">
        <UiComponentParagraph size="s">
          {{ props.componentWording.youMayAlsoLike }}
        </UiComponentParagraph>
      </UiComponentWrapper>
      <UiComponentWrapper class="list">
        <MoleculeIceBreakerCard
          v-for="{ id, color, duration, pattern, title, text, slug } in relatedIcebreakers"
          :key="`popular-icebreaker-${id}`"
          class="card"
          :color="color"
          :duration="duration"
          :pattern="pattern"
          :title="title"
          :text="text"
          :slug="slug"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Use Cases -->
    <UiComponentContainer class="use-cases">
      <UiComponentHeading :level="2">
        {{ props.parentPage.allUseCasesTitle }}
      </UiComponentHeading>
      <UiComponentWrapper class="list">
        <AtomAnchor
          v-for="{ id, heroShortTitle, slug } in props.allCategories"
          :key="`category-${id}`"
          class="item"
          :to="localePath({
            name: 'ice-breaker-games-slug',
            params: { slug },
          })"
        >
          {{ heroShortTitle }}
        </AtomAnchor>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > How To -->
    <UiComponentContainer class="how-to">
      <UiComponentHeading :level="2">
        {{ props.componentStepByStep.title }}
      </UiComponentHeading>
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="{ id, icon, title, text } in props.componentStepByStep.steps"
          :key="`item-${id}`"
          class="item"
        >
          <UiComponentWrapper class="head">
            <UiComponentIcon :type="icon.name" />
          </UiComponentWrapper>
          <UiComponentWrapper class="body">
            <UiComponentHeading :level="3">
              {{ title }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ text }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Get Started -->
    <UiComponentContainer class="get-started">
      <OrganismCallToAction
        :features="livestormFeatures"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ props.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ props.componentGetStarted.text }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>

    <!-- Section > Related Posts -->
    <OrganismHighlightedPosts
      v-if="props.page.relatedPostsItems.length >= 4 || props.componentRelatedPosts.items.length > 0"
      class="related-posts"
      :items="
        props.page.relatedPostsItems.length >= 4
          ? props.page.relatedPostsItems
          : props.componentRelatedPosts.items
      "
      :minutes-read="props.componentGlobal.minRead"
    >
      {{ props.page.relatedPostsTitle || props.componentRelatedPosts.title }}
    </OrganismHighlightedPosts>

    <!-- Section > Our Guides -->
    <OrganismHighlightedGuides
      v-if="props.allGuides.length > 0 || props.page.ourGuidesItems.length > 0"
      class="our-guides"
      :items="
        props.page.ourGuidesItems.length > 0
          ? props.page.ourGuidesItems
          : props.allGuides
      "
    >
      {{ props.page.ourGuidesTitle || props.componentOurGuides.title }}
    </OrganismHighlightedGuides>
  </UiComponentWrapper>
</template>

<script setup>
// Imports
import { gsap } from 'gsap'
import { StructuredText } from 'vue-datocms'

// Composables
const localePath = useLocalePath()
const { isLogged } = useIsLoggedIn()
const { $md } = useMarkdown()
const { $interpolate, $slugify } = useNuxtApp()
const { setResource } = useResource()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Props
const props = defineProps({
  allCategories: {
    required: true,
    type: Array,
  },
  allGuides: {
    required: true,
    type: Array,
  },
  allIcebreakers: {
    required: true,
    type: Array,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  componentOurGuides: {
    required: true,
    type: Object,
  },
  componentPromotion: {
    required: true,
    type: Object,
  },
  componentRelatedPosts: {
    required: true,
    type: Object,
  },
  componentStepByStep: {
    required: true,
    type: Object,
  },
  componentWording: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
  parentPage: {
    required: true,
    type: Object,
  },
})

// Refs
const $examples = ref()
const copied = ref(false)
const currentExampleIndex = ref(0)
const isAnimatingExamples = ref(false)

// Computed Properties
const exampleCount = computed(() => {
  return props.page.heroExamples.length
})

const imagesWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(Math.max(screenWidth.value - 48, 0), 840)
    default:
      return 840
  }
})

const livestormFeatures = computed(() => {
  return [props.componentGlobal.noCreditCard, props.componentGlobal.noInstall]
})

const relatedIcebreakers = computed(() => {
  return Array.from(
    new Set([
      ...props.allIcebreakers.filter(({ isPopular, tags }) => isPopular && tags.some(tag => props.page.heroSubCategories.includes(tag))),
      ...props.allIcebreakers.filter(({ isPopular, tags }) => !isPopular && tags.some(tag => props.page.heroSubCategories.includes(tag))),
      ...props.allIcebreakers.filter(({ isPopular }) => isPopular),
      ...props.allIcebreakers.filter(({ isPopular }) => !isPopular),
    ]),
  )
    .filter(({ slug }) => slug !== props.page.slug)
    .filter((_, index) => index < 8)
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: props.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const sidebarCTACard = computed(() => {
  return props.parentPage.sidebarCtaCard
    ? {
        id: props.parentPage.sidebarCtaCard.id,
        backgroundImage: props.parentPage.sidebarCtaCard.backgroundImage,
        callToAction: props.parentPage.sidebarCtaCard.resourceType.cta,
        coMarketing: false,
        description: props.parentPage.sidebarCtaCard.shortDescription,
        event: {
          name: 'Offer clicked',
          attributes: {
            label: 'Sidebar section CTA',
          },
        },
        image: props.parentPage.sidebarCtaCard.image,
        tagColor: props.parentPage.sidebarCtaCard.resourceType.color,
        tagIcon: props.parentPage.sidebarCtaCard.resourceType.icon,
        title: props.parentPage.sidebarCtaCard.shortTitle,
        to: props.parentPage.sidebarCtaCard.slug.includes('http') ? props.parentPage.sidebarCtaCard.slug : '#form-ebook',
        topic: props.parentPage.sidebarCtaCard.resourceTopic.name,
        trackName: props.parentPage.sidebarCtaCard.trackName || props.parentPage.sidebarCtaCard.trackNameFallback,
        type: props.parentPage.sidebarCtaCard.resourceType.name,
        url: localePath({
          name: 'file-slug',
          params: {
            slug: props.parentPage.sidebarCtaCard.ebook?.slug,
          },
        }),
      }
    : null
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaGetStartedForFree,
      }
})

// Methods
function copyToClipboard(value) {
  navigator.clipboard.writeText(value)
  copied.value = true
}

function getCallToActionBySlug(callToAction) {
  if (callToAction.to === `https://${publicRuntimeConfig.appBaseUrl}/#/signup`) {
    return signupOrUpgradeCallToAction.value
  }
  else if (callToAction.to === '#form-demo') {
    return requestDemoCallToAction.value
  }
  else {
    return callToAction
  }
}

function mod(n, m) {
  const remain = n % m
  return Math.floor(remain >= 0 ? remain : remain + m)
}

function navigateToSignupOrUpgrade(event) {
  if (!(event.target.classList.contains('button') || event.target.classList.contains('icon'))) {
    event.currentTarget.querySelector('.body .button').click()
  }
}

function renderBlock({ record, key }) {
  const UiComponentImage = resolveComponent('UiComponentImage')
  const MoleculeCallToAction = resolveComponent('MoleculeCallToAction')
  const MoleculeCustomerQuote = resolveComponent('MoleculeCustomerQuote')
  const MoleculeDefinition = resolveComponent('MoleculeDefinition')
  const MoleculeTextImage = resolveComponent('MoleculeTextImage')
  const MoleculeTextYoutubeIframe = resolveComponent('MoleculeTextYoutubeIframe')
  const OrganismContentCard = resolveComponent('OrganismContentCard')
  const OrganismHero = resolveComponent('OrganismHero')
  const OrganismPlayerVideo = resolveComponent('OrganismPlayerVideo')
  const OrganismRelatedPosts = resolveComponent('OrganismRelatedPosts')
  const OrganismRichText = resolveComponent('OrganismRichText')
  const OrganismTabs = resolveComponent('OrganismTabs')
  const OrganismTestimonials = resolveComponent('OrganismTestimonials')
  const UiComponentWrapper = resolveComponent('UiComponentWrapper')

  switch (record.__typename) {
    case 'AtomImageRecord':
      return h(
        UiComponentWrapper,
        {
          key,
          class: 'image-full',
        },
        () => [
          h(UiComponentImage, {
            alt: record.image.alt,
            expectedSize: imagesWidth.value,
            height: record.image.height,
            orientation: record.image.width >= record.image.height ? 'landscape' : 'portrait',
            source: record.image.url,
            width: record.image.width,
          }),
        ],
      )
    case 'AtomVideoRecord':
      return h(
        UiComponentWrapper,
        {
          key,
          class: 'video-full',
        },
        () => [
          h(OrganismPlayerVideo, {
            posterSrc: record.video.data.thumbnailUrl,
            videoSrc: record.video.data.streamingUrl,
          }),
        ],
      )
    case 'ContentYoutubeVideoRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismRichText, {
            class: 'w-full',
            content: [
              {
                __typename: 'ContentYoutubeVideoRecord',
                youtubeLink: record.youtubeLink,
              },
            ],
          }),
        ],
      )
    case 'MoleculeContentHeaderRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismHero, {
            class: 'content-header',
            alignment: 'left',
            level: 2,
            primaryButton: getCallToActionBySlug(
              record.callToAction,
            ),
          }, {
            heading: () => record.heading,
            paragraph: () => record.paragraph,
            tag: () => (record.tag === '' ? undefined : record.tag),
          }),
        ],
      )
    case 'MoleculeDefinitionRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(
            MoleculeDefinition,
            {
              level: record.titleLevel,
              title: record.title,
            },
            () => [
              h('div', {
                innerHTML: $md.render(record.content),
              }),
            ],
          ),
        ],
      )
    case 'MoleculeQuoteRecord':
      return h(MoleculeCustomerQuote, {
        key,
        quote: {
          content: record.testimonial.quote,
          customer: {
            avatar: {
              ...record.testimonial.avatar,
              src: record.testimonial.avatar.url,
            },
            name: record.testimonial.fullname,
            position: record.testimonial.occupation,
          },
          logo: {
            ...record.testimonial.logo,
            src: record.testimonial.logo.url,
          },
        },
      })
    case 'MoleculeSmallCallToActionRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(MoleculeCallToAction, {
            callToAction: {
              ...signupOrUpgradeCallToAction.value,
              label: 'Small section CTA',
            },
            ...props.componentPromotion,
          }, {
            heading: () => record.heading,
            paragraph: () => record.paragraph,
          }),
        ],
      )
    case 'MoleculeTextImageRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(MoleculeTextImage, {
            direction: record.direction,
            image: record.image,
            level: record.titleLevel,
            maxImageHeight: 366,
            maxImageWidth: 290,
          }, {
            heading: () => record.heading,
            paragraph: () =>
              h(OrganismRichText, {
                class: 'whitespace-normal',
                content: [
                  {
                    __typename: 'ContentTextHtmlRecord',
                    text: record.paragraph,
                  },
                ],
              }),
          }),
        ],
      )
    case 'MoleculeTextOnlyRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h('div', {
            innerHTML: $md.render(record.content),
          }),
        ],
      )
    case 'MoleculeTextVideoRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(MoleculeTextYoutubeIframe, {
            level: record.titleLevel,
            video: {
              ...record.video,
            },
          }, {
            heading: () => record.heading,
            paragraph: () =>
              h(OrganismRichText, {
                class: 'whitespace-normal',
                content: [
                  {
                    __typename: 'ContentTextHtmlRecord',
                    text: record.paragraph,
                  },
                ],
              }),
          }),
        ],
      )
    case 'OrganismContentCardRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismContentCard, {
            as: 'paragraph',
            content: {
              backgroundImage: record.content.backgroundImage,
              callToAction: record.content.resourceType.cta,
              description: record.content.shortDescription,
              image: record.content.image,
              tagColor: record.content.resourceType.color,
              tagIcon: record.content.resourceType.icon,
              title: record.content.title,
              to: record.content.slug.includes('http')
                ? record.content.slug
                : localePath({
                    name: 'resources-type-slug',
                    params: {
                      slug: record.content.slug,
                      type: $slugify(
                        record.content.resourceType.name,
                      ),
                    },
                  }),
              type: record.content.resourceType.name,
            },
            highlighted: true,
          }),
        ],
      )
    case 'OrganismRelatedPostListRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismRelatedPosts, {
            posts: record.posts
              .filter((_, index) => index < 2)
              .map(post => ({
                category: post.blogPostCategory[0]?.name,
                content: post.content,
                coverImage: post.coverWithImgix || post.coverImage,
                date: post.date,
                slug: `blog/${post.slug}`,
                title: post.title,
              })),
          }, {
            default: () => record.heading,
          }),
        ],
      )
    case 'OrganismTabRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismTabs, {
            tabs: record.tabs.map(tab => ({
              id: tab.id,
              title: tab.title,
              content: $md.render(tab.content),
            })),
          }),
        ],
      )
    case 'OrganismTestimonialListRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismTestimonials, {
            testimonials: record.testimonials
              .filter(testimonial => testimonial.authorUserStory)
              .map(testimonial => ({
                customer: {
                  avatar: {
                    alt: `${testimonial.authorUserStory}' Avatar`,
                    height: testimonial.authorPicture.height,
                    url: testimonial.authorPicture.url,
                    width: testimonial.authorPicture.width,
                  },
                  name: testimonial.authorUserStory,
                  position: testimonial.authorPosition,
                },
                logo: {
                  alt: `${testimonial.name}' logo`,
                  height: testimonial.logoColor.height,
                  url: testimonial.logoColor.url,
                  width: testimonial.logoColor.width,
                },
                quote: testimonial.quote,
              })),
          }),
        ],
      )
    default:
      return null
  }
}

function showPreviousExample() {
  if (isAnimatingExamples.value || exampleCount.value < 2) {
    return
  }

  isAnimatingExamples.value = true

  gsap.set($examples.value[mod(currentExampleIndex.value - 1, exampleCount.value)].$el, {
    display: 'flex',
    rotate: -3.789,
    zIndex: 0,
  })
  gsap.to($examples.value[mod(currentExampleIndex.value - 1, exampleCount.value)].$el, {
    duration: 0.6,
    ease: 'expo.in',
    onComplete: () => {
      gsap.set($examples.value[mod(currentExampleIndex.value - 1, exampleCount.value)].$el, { zIndex: 20 })
      gsap.to($examples.value[mod(currentExampleIndex.value - 1, exampleCount.value)].$el, {
        duration: 0.4,
        ease: 'expo.out',
        onComplete: () => {
          currentExampleIndex.value = mod(currentExampleIndex.value - 1, exampleCount.value)
          isAnimatingExamples.value = false
        },
        rotate: 0,
        xPercent: 0,
      })
    },
    xPercent: 110,
  })
  gsap.to($examples.value[mod(currentExampleIndex.value, exampleCount.value)].$el, {
    duration: 1,
    ease: 'expo.inOut',
    rotate: 6.582,
    zIndex: 10,
  })
  gsap.to($examples.value[mod(currentExampleIndex.value + 1, exampleCount.value)].$el, {
    duration: 1,
    ease: 'expo.inOut',
    rotate: -3.789,
    zIndex: 0,
  })
  gsap.set($examples.value[mod(currentExampleIndex.value + 2, exampleCount.value)].$el, {
    display: 'none',
    rotate: -3.789,
    zIndex: 0,
  })
}

function showNextExample() {
  if (isAnimatingExamples.value || exampleCount.value < 2) {
    return
  }

  isAnimatingExamples.value = true

  gsap.to($examples.value[mod(currentExampleIndex.value, exampleCount.value)].$el, {
    duration: 0.6,
    ease: 'expo.in',
    onComplete: () => {
      gsap.set($examples.value[mod(currentExampleIndex.value, exampleCount.value)].$el, { zIndex: 0 })
      gsap.to($examples.value[mod(currentExampleIndex.value, exampleCount.value)].$el, {
        duration: 0.4,
        ease: 'expo.out',
        onComplete: () => {
          gsap.set($examples.value[mod(currentExampleIndex.value, exampleCount.value)].$el, { display: 'none' })
          gsap.set($examples.value[mod(currentExampleIndex.value + 3, exampleCount.value)].$el, {
            display: 'flex',
            rotate: -3.789,
            zIndex: 0,
          })
          currentExampleIndex.value = mod(currentExampleIndex.value + 1, exampleCount.value)
          isAnimatingExamples.value = false
        },
        rotate: -3.789,
        xPercent: 0,
      })
    },
    rotate: 3.789,
    xPercent: 110,
  })
  gsap.to($examples.value[mod(currentExampleIndex.value + 1, exampleCount.value)].$el, {
    duration: 1,
    ease: 'expo.inOut',
    rotate: 0,
    zIndex: 20,
  })
  gsap.to($examples.value[mod(currentExampleIndex.value + 2, exampleCount.value)].$el, {
    duration: 1,
    ease: 'expo.inOut',
    rotate: 6.582,
    zIndex: 10,
  })
}

// Lifecycle Hooks
onMounted(() => {
  setResource({
    ...sidebarCTACard.value,
    event: {
      name: 'Offer clicked',
      attributes: {
        label: 'Thank you pop up CTA',
      },
    },
    to: sidebarCTACard.value.url,
  })
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex flex-col items-center overflow-hidden;

  @screen xl {
    @apply overflow-visible;
  }

  .jumbotron {
    @apply flex-col gap-12 mt-8;

    @screen xl {
      @apply flex-row flex-wrap items-center justify-between gap-0;
    }

    .hero {
      @apply flex-col;

      @screen xl {
        @apply w-span7;
      }

      .tags {
        @apply gap-3;

        .tag {
          @apply w-auto px-2 py-1 bg-livestorm-blue-100 font-medium text-livestorm-blue-700 text-xs leading-4 rounded-xs;
        }
      }

      .h1 {
        @apply mt-8 text-3xl leading-10;

        @screen mdminus {
          @apply font-medium text-3xl leading-10;
        }

        @screen mdplus {
          @apply font-normal text-5xl leading-16;
        }

        :deep(b),
        :deep(strong) {
          @screen mdminus {
            @apply font-medium;
          }

          @screen mdplus {
            @apply font-normal;
          }
        }

        & + p {
          @apply mt-2 text-grey-blue-600 leading-7;
        }
      }

      .metadata {
        @apply flex-col gap-4 mt-10 p-6 bg-grey-blue-100 border-2 border-grey-blue-200 rounded-2xl;

        @screen md {
          @apply flex-row flex-wrap gap-x-10 gap-y-4;
        }

        .item {
          @apply items-center gap-2;

          @screen md {
            width: calc((100% - 40px) / 2);
          }

          .icon {
            @apply text-grey-blue-600 text-base leading-none;
          }

          p {
            @apply text-grey-blue-600 text-base leading-[normal];
          }
        }
      }
    }

    .examples {
      @apply relative flex-col items-center h-[418px];

      @screen xl {
        @apply w-span5;
      }

      .card {
        @apply hidden absolute top-0 w-auto;

        &:nth-child(1),
        &:nth-child(2),
        &:nth-child(3) {
          @apply flex;
        }

        &:nth-child(1) {
          @apply flex z-20;
        }

        &:nth-child(2),
        &:nth-child(3) {
          @apply flex transform origin-center rotate-[6.582deg] z-10;
        }

        &:nth-child(3) {
          @apply -rotate-[3.789deg] z-0;
        }

        &.blizzard {
          .inner {
            @apply bg-blizzard-600;
          }
        }

        &.live-red {
          .inner {
            @apply bg-live-red-600;
          }
        }

        &.sirocco {
          .inner {
            @apply bg-sirocco-600;
          }
        }

        &.winter-green {
          .inner {
            @apply bg-winter-green-500;
          }
        }

        &.brick {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/brick.svg')] bg-[length:61px_60px];
            }
          }
        }

        &.circle {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/circle.svg')] bg-[length:60px_60px];
            }
          }
        }

        &.concentric {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/concentric.svg')] bg-[length:61px_60px];
            }
          }
        }

        &.cross {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/cross.svg')] bg-[length:61px_60px];
            }
          }
        }

        &.double-bubble {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/double-bubble.svg')] bg-[length:61px_60px];
            }
          }
        }

        &.hexagon-1 {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/hexagon-1.svg')] bg-[length:41px_60px];
            }
          }
        }

        &.hexagon-2 {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/hexagon-2.svg')] bg-[length:36px_60px];
            }
          }
        }

        &.lines {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/lines.svg')] bg-[length:61px_60px];
            }
          }
        }

        &.scales {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/scales.svg')] bg-[length:61px_60px];
            }
          }
        }

        &.sprinkles {
          .inner {
            &::before {
              @apply bg-[url('~/assets/pattern/sprinkles.svg')] bg-[length:61px_60px];
            }
          }
        }

        .inner {
          @apply relative flex-col max-w-[290px] h-[374px] w-[290px] p-8 border-[6px] border-white rounded-2xl shadow-light3;

          &::before {
            @apply absolute inset-0 bg-repeat opacity-5 content-[''] z-0;
          }

          &::after {
            @apply absolute inset-0 opacity-[0.16] content-[''] z-10;
            background: linear-gradient(141deg, rgba(255, 255, 255, 0.00) 28.5%, #FFF 48.57%, rgba(255, 255, 255, 0.00) 69%);
          }

          .head {
            @apply font-medium text-white text-2xl leading-[normal] z-20;
          }

          .body {
            @apply relative mt-6 z-20;

            &::before {
              @apply absolute -inset-[8px] bg-white/15 rounded-xs content-[''] z-0;
            }

            .question {
              @apply text-white text-origin leading-7  z-10;
            }

            .copy {
              @apply absolute -bottom-[32px] right-[8px] gap-1 w-auto p-[6px] bg-white rounded-md cursor-pointer z-20;

              &.copied {
                &::before {
                  @apply absolute left-1/2 -top-3 flex h-4 w-4 bg-winter-green-900 transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 rounded-sm content-[''] z-0;
                }

                &::after {
                  @apply absolute left-1/2 -top-3 w-32 px-3 py-1 bg-winter-green-900 text-center text-xs text-white leading-4 rounded-md transform -translate-x-1/2 -translate-y-full content-[attr(data-copied)] z-10;
                }
              }

              .icon {
                @apply text-grey-blue-600 text-base leading-none;
              }

              p {
                @apply text-grey-blue-600 text-xs leading-4;
              }
            }
          }

          .footer {
            @apply mt-auto items-center justify-between z-20;

            .navigation {
              @apply items-center gap-3;

              .icon {
                @apply text-white text-base leading-none cursor-pointer;

                &.disabled {
                  @apply opacity-40 cursor-default;
                }
              }

              .status {
                @apply font-medium text-white leading-7;
              }
            }

            .logo {
              @apply w-auto;
            }
          }
        }
      }
    }

    .illustrations {
      @apply relative flex-col items-center h-[418px];

      @screen xl {
        @apply w-span5;
      }

      .card {
        @apply flex absolute top-0 w-auto transform origin-center;

        &:nth-child(1) {
          @apply rotate-[11.726deg] z-20;
        }

        &:nth-child(2) {
          @apply z-10;
        }

        &:nth-child(3) {
          @apply -rotate-[6.924deg] z-0;
        }

        &.brick {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/brick.svg')] bg-[length:61px_60px];
              }
            }
          }
        }

        &.circle {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/circle.svg')] bg-[length:60px_60px];
              }
            }
          }
        }

        &.concentric {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/concentric.svg')] bg-[length:61px_60px];
              }
            }
          }
        }

        &.cross {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/cross.svg')] bg-[length:61px_60px];
              }
            }
          }
        }

        &.double-bubble {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/double-bubble.svg')] bg-[length:61px_60px];
              }
            }
          }
        }

        &.hexagon-1 {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/hexagon-1.svg')] bg-[length:41px_60px];
              }
            }
          }
        }

        &.hexagon-2 {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/hexagon-2.svg')] bg-[length:36px_60px];
              }
            }
          }
        }

        &.lines {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/lines.svg')] bg-[length:61px_60px];
              }
            }
          }
        }

        &.scales {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/scales.svg')] bg-[length:61px_60px];
              }
            }
          }
        }

        &.sprinkles {
          .outer {
            .inner {
              &::before {
                @apply bg-[url('~/assets/pattern/sprinkles.svg')] bg-[length:61px_60px];
              }
            }
          }
        }

        .outer {
          @apply relative flex-col h-[374px] w-[290px] p-3 bg-winter-green-900 rounded-[21.9px] shadow-light3;

          .inner {
            @apply items-center justify-center h-full w-full bg-winter-green-900 border border-white/40 rounded-2xl;

            &::before {
              @apply absolute inset-3 bg-repeat opacity-5 rounded-2xl content-[''] z-0;
            }

            .logo {
              @apply opacity-20;
            }
          }
        }
      }
    }
  }

  .content {
    @apply flex-col gap-20 mt-14;

    @screen md {
      @apply mt-26;
    }

    @screen xl {
      @apply flex-row flex-wrap gap-0 justify-between;
    }

    :deep(.main) {
      @apply flex-col;

      @screen xl {
        @apply w-span8;
      }

      > div {
        @apply flex flex-col gap-10 items-center w-full;

        .content-card {
          @screen xl {
            @apply max-h-none h-auto;
          }

          .head {
            @screen xl {
              @apply flex-shrink-0 h-full max-h-[274px] w-[290px];
            }

            .ebook {
              &.landscape {
                @screen xl {
                  width: calc(100% - 38px);
                }
              }
            }
          }

          .body {
            @screen xl {
              @apply w-full px-10 !important;
            }

            .button {
              .h2 {
                @screen xl {
                  @apply line-clamp-2;
                }
              }
            }

            .description {
              @screen xl {
                @apply line-clamp-2;
              }
            }
          }
        }

        .image-full {
          @apply justify-center;
        }

        .video-full {
          @apply justify-center;

          .video {
            @apply h-auto max-w-840 rounded-2xl;

            video {
              @apply h-auto rounded-2xl;
            }
          }
        }

        .quote {
          @apply bg-grey-blue-100;
        }

        .small-call-to-action {
          .left {
            .h4 {
              @apply font-medium text-white text-xl leading-7 !important;

              @screen mdminus {
                @apply text-2xl leading-8 !important;
              }

              @screen mdplus {
                @apply text-2xl leading-8 !important;
              }
            }

            .h4 + p {
              @apply mt-2 text-grey-blue-100 !important;
            }
          }
        }

        .text-image {
          @screen md {
            @apply flex-row items-center justify-between;
          }

          &.left {
            .image {
              @screen md {
                @apply order-first mr-10 mt-0;
              }
            }
          }

          &.right {
            .image {
              @screen md {
                @apply ml-10 mt-0;
              }
            }
          }

          .text {
            @screen md {
              @apply w-full p-0 m-0;
            }
          }

          .text .rich-text p {
            @apply text-grey-blue-600 !important;
          }

          .image {
            @apply flex-shrink-0 h-[366px] bg-grey-blue-100;

            @screen md {
              @apply w-[290px];
            }
          }
        }

        .text-iframe {
          @screen md {
            @apply flex-col items-start;
          }

          .iframe {
            @apply order-first bg-grey-blue-100 rounded-4xl overflow-hidden;

            @screen md {
              @apply max-w-none w-full pb-[56.25%];
            }

            @screen xl {
              @apply w-[730px];
            }
          }

          .text {
            @apply pl-0 mt-8;
          }
        }
      }

      .related-posts {
        @apply relative flex-col gap-4 p-8 bg-grey-blue-100 rounded-4xl;

        @screen md {
          @apply p-12;
        }

        .head {
          .line {
            @apply hidden;
          }

          p {
            @apply px-0 font-medium text-xl text-winter-green-900 leading-7 normal-case !important;

            @screen md {
              @apply text-2xl leading-8 !important;
            }
          }
        }

        .body {
          @apply flex-col gap-10 mt-0;

          @screen md {
            @apply flex-row flex-wrap justify-between;
          }

          .related-post {
            @apply w-full;

            @screen md {
              @apply mx-0;
              width: calc((100% - 40px) / 2);
            }

            .tag {
              @apply mt-4 h-6 px-2 bg-blizzard-100 text-blizzard-700 text-xs transform-none leading-4 rounded-xs;
            }

            .anchor {
              @apply mt-3;

              .h4 {
                @apply font-medium text-winter-green-900 text-origin leading-7 !important;

                @screen md {
                  @apply text-xl !important;
                }
              }
            }

            .description {
              @apply mt-1 text-sm text-grey-blue-600 leading-6 !important;

              .date {
                @apply hidden;
              }
            }
          }
        }
      }

      .call-to-action {
        .content {
          p {
            @apply text-livestorm-blue-200 !important;
          }

          .items {
            li {
              &:before {
                content: "\e83f";
              }
            }
          }
        }
      }

      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply font-medium text-winter-green-900 whitespace-pre-line;

        b,
        strong {
          @apply font-medium text-livestorm-blue-700;
        }
      }

      h2 {
        @apply text-2xl leading-8;

        @screen mdminus {
          font-size: 28px;
          line-height: 34px;
        }

        @screen mdplus {
          @apply text-3xl leading-10;
        }
      }

      h3 {
        @apply text-xl leading-7;

        @screen mdminus {
          @apply text-2xl leading-8;
        }

        @screen mdplus {
          @apply text-2xl leading-8;
        }
      }

      h4 {
        @apply text-origin leading-7;

        @screen mdminus {
          @apply text-xl;
        }

        @screen mdplus {
          @apply text-xl;
        }
      }

      h5,
      h6 {
        @apply text-origin leading-7;
      }

      p,
      .rich-text p {
        @apply font-normal text-grey-blue-600 text-origin leading-7 !important;
      }

      blockquote {
        @apply p-8 bg-livestorm-blue-100 rounded-4xl;

        @screen md {
          @apply p-10;
        }

        p {
          @apply inline text-center text-livestorm-blue-700 text-xl leading-[160%] !important;

          &::before,
          &::after {
            @apply inline;
          }

          &::before {
            @apply content-['“'];
          }

          &::after {
            @apply content-['”'];
          }
        }
      }

      ol {
        @apply pl-8;

        li {
            @apply font-normal text-origin text-grey-blue-600 leading-7 list-decimal list-inside  !important;

            &::marker {
              @apply text-lg leading-7 font-medium text-livestorm-blue-700;
            }
          }
      }

      ul {
        @apply pl-8;

        li {
          @apply block font-normal text-origin text-grey-blue-600 leading-7 !important;

          &:before {
            @apply inline-block w-3 h-3 bg-livestorm-blue-700 mr-3 content-[''] rounded-[3px];
          }
        }
      }

      strong {
        @apply font-medium;
      }

      a {
        @apply text-livestorm-blue-700;

        &:hover {
          @apply text-livestorm-blue-800;
        }

        &.button {
          &.primary {
            @apply text-white;
          }
        }
      }

      h2 + h2,
      h2 + h3,
      h3 + h2,
      h3 + h3,
      h4 + h2,
      h4 + h3,
      h5 + h2,
      h5 + h3,
      h6 + h2,
      h6 + h3,
      h2 + blockquote,
      h3 + blockquote,
      h2 + p,
      h3 + p,
      h2 + ol,
      h3 + ol,
      h2 + ul,
      h3 + ul {
        @apply mt-4;
      }

      h2 + h4,
      h2 + h5,
      h2 + h6,
      h3 + h4,
      h3 + h5,
      h3 + h6,
      h4 + h4,
      h4 + h5,
      h5 + h6,
      h5 + h4,
      h5 + h5,
      h5 + h6,
      h5 + h6,
      h6 + h4,
      h6 + h5,
      h6 + h6,
      h4 + blockquote,
      h5 + blockquote,
      h6 + blockquote,
      h4 + p,
      h5 + p,
      h6 + p,
      h4 + ol,
      h5 + ol,
      h6 + ol,
      h4 + ul,
      h5 + ul,
      h6 + ul,
      p + ol,
      p + ul {
        @apply mt-3;
      }

      p + h2,
      ol + h2,
      ul + h2 {
        @apply mt-26;
      }

      p + h3,
      p + h4,
      p + h5,
      p + h6,
      ol + h3,
      ol + h4,
      ol + h5,
      ol + h6,
      ul + h3,
      ul + h4,
      ul + h5,
      ul + h6 {
        @apply mt-8;
      }

      blockquote + p,
      p + blockquote,
      p + p,
      ol + p,
      ul + p,
      li + li {
        @apply mt-4;
      }
    }

    .aside {
      @apply flex-col gap-8;

      @screen xl {
        @apply sticky top-24 self-start w-span3;
      }

      .try {
        @apply flex-col bg-white border-2 border-grey-blue-200 rounded-4xl cursor-pointer overflow-hidden transition-all ease-out duration-500;

        &:hover {
          @apply shadow-light4;
        }

        .head {
          @apply items-center justify-center bg-grey-blue-100;

          picture {
            @apply rounded-none;

            &.zoom-out {
              @apply transform origin-center scale-85 -rotate-3;
            }
          }
        }

        .body {
          @apply flex-col gap-2 p-6 pb-4;

          .button {
            @apply self-start h-[50px] px-0 text-livestorm-blue-700;
          }
        }
      }

      .resource {
        @apply hidden;

        @screen xl {
          @apply flex flex-col h-auto;

          :deep(.head) {
            @apply h-60;
          }

          :deep(.body) {
            @apply p-6;

            .tag-with-image {
              @apply gap-1 h-6 px-2 font-medium text-xs leading-4;

              i {
                @apply m-0 text-xs leading-none;
              }
            }

            .description {
              @apply mt-2;
            }

            .button {
              &.text {
                @apply mt-2 pt-4 pb-2 text-origin leading-none;
              }
            }
          }
        }
      }
    }

    .footer {
      @apply flex-col -mt-8;

      @screen xl {
        @apply hidden;
      }

      .resource {
        @apply flex flex-col h-auto;

        @screen md {
          @apply flex-row h-[343px];
        }

        :deep(.head) {
          @screen lg {
            @apply h-[343px] w-[343px];
          }
        }

        :deep(.body) {
          @apply p-6;

          .tag-with-image {
            @apply gap-1 h-6 px-2 font-medium text-xs leading-4;

            i {
              @apply m-0 text-xs leading-none;
            }
          }

          .description {
            @apply mt-2;
          }

          .button {
            &.text {
              @apply mt-2 pt-4 pb-2 text-origin leading-none;
            }
          }
        }
      }
    }
  }

  .you-may-like {
    @apply flex-col mt-14 gap-8;

    @screen md {
      @apply mt-26 gap-12;
    }

    .title {
      @apply relative justify-center items-center;

      &::before {
        @apply absolute left-0 right-0 h-0.5 bg-[#C8D3DA] content-[''] z-0;
      }

      p {
        @apply px-4 bg-white font-medium text-center text-[#4D5B66] leading-6 uppercase z-10;
      }
    }

    .list {
      @apply flex-col gap-8;

      @screen md {
        @apply flex-row flex-wrap gap-10;
      }

      .card {
        @screen md {
          width: calc((100% - 40px) / 2);
        }

        @screen xl {
          width: calc((100% - 120px) / 4);
        }
      }
    }
  }

  .use-cases {
    @apply flex flex-col gap-10 mt-26;

    @screen md {
      @apply gap-14 mt-46;
    }

    .h2 {
      @apply self-center text-center;

      @screen md {
        @apply self-start text-left;
      }

      @screen xl {
        @apply self-center text-center;
      }
    }

    .list {
      @apply flex-wrap justify-center gap-4;

      .item {
        @apply px-4 py-1 font-bold text-livestorm-blue-700 text-sm leading-6;
      }
    }
  }

  .how-to {
    @apply flex flex-col gap-12 mt-26;

    @screen md {
      @apply mt-46;
    }

    @screen xl {
      @apply items-center;
    }

    .list {
      @apply flex-col gap-10;

      @screen xl {
        @apply flex-row flex-nowrap;
      }

      .item {
        @apply flex-col gap-6;

        @screen md {
          @apply flex-row flex-nowrap;
        }

        @screen xl {
          @apply flex-col flex-wrap items-center;
        }

        .head {
          @apply flex-shrink-0 items-center justify-center h-20 w-20 bg-winter-green-100 rounded-3xl;

          .icon {
            @apply text-winter-green-700 text-2xl leading-none;
          }
        }

        .body {
          @apply flex-col gap-4;

          .h3 {
            @apply text-xl leading-7;

            @screen mdminus {
              @apply text-2xl leading-8;
            }

            @screen mdplus {
              @apply text-2xl leading-8;
            }

            @screen xl {
              @apply text-center;
            }
          }

          p {
            @apply text-grey-blue-600;

            @screen xl {
              @apply text-center;
            }
          }
        }
      }
    }
  }

  .get-started {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }
  }

  .related-posts {
    @apply mt-14;

    @screen md {
      @apply mt-26;
    }
  }
}
</style>
